import { useEffect, useState, Children } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowLeft, Save, Wand2, Loader2, Copy, Eye, Plus, Trash2, LogOut, Type as TypeIcon, Sparkles, Download, FileDown, BookOpen, Lock, X, ChevronUp, ChevronDown, GripVertical } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { getTheme, getConcept, DEFAULT_DRESSCODES, THEME_LIST } from "@/lib/themes";
import { API, createInvitation, generateImage, generateSticker, getInvitation, updateInvitation } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import ImageUpload, { resolveImageUrl } from "@/components/ImageUpload";
import AudioUpload from "@/components/AudioUpload";
import StyleCustomizer from "@/components/StyleCustomizer";
import BlockOrderEditor from "@/components/BlockOrderEditor";
import GlobalBackground from "@/components/GlobalBackground";
import StickerButton from "@/components/StickerButton";
import { buildStyleVars } from "@/lib/fonts";
import { buildThemeFromImage, rgbChannels, hexToRgb } from "@/lib/palette";
import { useThemeVars } from "@/lib/useThemeVars";
import { formatTime12h } from "@/lib/utils";
import InvitationView from "@/pages/InvitationView";
import { IframePreview } from "@/components/IframePreview";


export default function Editor() {
  const { theme: themeParam, concept: conceptParam, id: existingId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [inv, setInv] = useState(null);
  // Editor-page appearance — a LOCAL preference (this browser only), not saved to the invitation.
  // Stored PER invitation so a config set on one invitation doesn't bleed into another.
  // Defaults follow the chosen theme; the user can override to fix contrast on dark themes.
  const edUiKey = `fn-editor-ui:${existingId || "new"}`;
  const [edUi, setEdUi] = useState(() => {
    try { return JSON.parse(localStorage.getItem(edUiKey) || "{}"); } catch { return {}; }
  });
  // Reload prefs when switching between invitations (the component instance is reused by the router).
  useEffect(() => {
    try { setEdUi(JSON.parse(localStorage.getItem(edUiKey) || "{}")); } catch { setEdUi({}); }
  }, [edUiKey]);
  const setEd = (patch) => setEdUi((prev) => {
    const next = { ...prev, ...patch };
    try { localStorage.setItem(edUiKey, JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });

  useEffect(() => {
    (async () => {
      try {
        if (existingId) {
          const data = await getInvitation(existingId);
          setInv(data);
        } else {
          const theme = getTheme(themeParam);
          const concept = getConcept(themeParam, conceptParam);
          if (!theme || !concept) {
            navigate("/crear");
            return;
          }
          setInv({
            theme: theme.id,
            concept: concept.id,
            title: theme.id === "boda" ? "Ana & Luis" : theme.id === "baby_reveal" ? "Baby Reveal · José Arath" : theme.id === "xv_anos" ? "Mis XV · Sofía" : theme.id === "cumpleanos" ? "Cumple mágico" : "Bautizo",
            subtitle: theme.heroCopy,
            hosts: "",
            hero_image_url: concept.image,
            generated_bg_url: "",
            photo_urls: [],
            event_date: "",
            event_time: "19:00",
            event_duration_hours: 5,
            venue_name: "",
            address: "",
            gmaps_url: "",
            dresscode: { ...(DEFAULT_DRESSCODES[theme.id] || { style: "", note: "", colors: [] }), pinterest_board_url: "" },
            message: theme.id === "boda" ? "Con la bendición de Dios y nuestros padres, nos gustaría compartir contigo el inicio de nuestro para siempre." : theme.id === "baby_reveal" ? "Estamos por conocer al ser más amado de nuestras vidas. Acompáñanos a celebrar y a descubrir juntos si viene en rosa o en azul." : "Nos encantaría contar con tu presencia en este día tan especial.",
            gift: { message: "Si deseas hacernos llegar un detalle, tu presencia ya es el mejor regalo.", bank_details: "", registry_url: "" },
            host_whatsapp: "",
            reveal_prediction_enabled: theme.id === "baby_reveal",
            music: { provider: "", url: "", audio_url: "", autoplay: true },
            itinerary: [],
            itinerary_show_times: true,
            hashtag: "",
            padrinos: [],
            lodging: "",
            kids_policy: "",
            parking_info: "",
            extra_fields: [],
            playlist_enabled: false,
            live_stream_url: "",
            guestbook_enabled: false,
            custom_style: { headline_font: "", script_font: "", body_font: "", title_size: "", text_color: "", accent_color: "" },
            section_backgrounds: {},
            global_bg_url: "",
            global_bg_opacity: 0.3,
            block_order: [],
            hidden_blocks: [],
            share_enabled: true,
            show_theme_label: true,
            show_invite_badge: true,
            is_active: false,
            auto_deactivate_at: null,
            guests_share_enabled: false,
            guests_share_password: "",
          });
        }
      } catch (e) {
        toast.error("No se pudo cargar la invitación");
      } finally {
        setLoading(false);
      }
    })();
  }, [themeParam, conceptParam, existingId, navigate]);

  const [aiPrompt, setAiPrompt] = useState("");
  const [aiRefs, setAiRefs] = useState([]);
  const [autoTheme, setAutoTheme] = useState(true);
  useThemeVars(inv?.custom_style);

  const update = (patch) => setInv((prev) => ({ ...prev, ...patch }));
  const updateDress = (patch) => setInv((prev) => ({ ...prev, dresscode: { ...prev.dresscode, ...patch } }));
  const updateGift = (patch) => setInv((prev) => ({ ...prev, gift: { ...prev.gift, ...patch } }));
  const updateMusic = (patch) => setInv((prev) => ({ ...prev, music: { ...prev.music, ...patch } }));
  const updateStyle = (nextStyle) => setInv((prev) => ({ ...prev, custom_style: nextStyle }));

  // Reads the dominant colors of an image and re-themes the whole invitation + editor
  const applyThemeFromImage = async (imageUrl, { silent = false } = {}) => {
    try {
      const { palette, variants, ...theme } = await buildThemeFromImage(resolveImageUrl(imageUrl));
      setInv((prev) => ({ ...prev, custom_style: { ...(prev.custom_style || {}), ...theme } }));
      if (!silent) toast.success("Colores tomados de tu imagen");
    } catch {
      if (!silent) toast.error("No pudimos leer los colores de la imagen");
    }
  };

  const autoDeadlinePassed = (() => {
    if (!inv?.auto_deactivate_at) return false;
    const d = new Date(inv.auto_deactivate_at);
    return !isNaN(d.getTime()) && d.getTime() <= Date.now();
  })();

  // Re-activating must also clear an expired deadline, otherwise guests keep seeing the closed screen
  const handleActiveToggle = (checked) => {
    if (checked && autoDeadlinePassed) {
      update({ is_active: true, auto_deactivate_at: null });
      toast.success("Invitación activada — quitamos la fecha límite porque ya había pasado");
      return;
    }
    update({ is_active: checked });
  };

  const handleThemeChange = (themeId) => {
    if (!inv || inv.theme === themeId) return;
    const t = getTheme(themeId);
    if (!t) return;
    const concept = getConcept(themeId, inv.concept)?.id || t.concepts[0].id;
    update({ theme: themeId, concept });
    toast.success(`Temática cambiada a ${t.name} — el contador ahora dice “${inv.countdown_label || t.countdownLabel}”`);
  };


  const handleHeroChange = async (v) => {    // An uploaded hero always wins over the AI-generated background
    update({ hero_image_url: v, generated_bg_url: "" });
    if (v && autoTheme) await applyThemeFromImage(v);
  };

  const handleDownloadHero = async () => {
    const url = inv?.generated_bg_url || inv?.hero_image_url;
    if (!url) { toast.error("No hay imagen de portada para descargar"); return; }
    try {
      const resolved = resolveImageUrl(url);
      const resp = await fetch(resolved);
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      const themeSlug = (inv.theme || "invitacion").replace(/\s+/g, "-");
      const ext = (blob.type && blob.type.split("/")[1]) || "jpg";
      a.download = `portada-${themeSlug}-${Date.now()}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
      toast.success("Imagen descargada");
    } catch (e) {
      toast.error("No se pudo descargar la imagen");
    }
  };

  const handleGenerate = async () => {
    if (!inv) return;
    try {
      setGenLoading(true);
      toast.info("Generando arte único con IA...", { duration: 2500 });
      const concept = getConcept(inv.theme, inv.concept);
      const res = await generateImage({
        theme: inv.theme,
        concept: concept?.name || inv.concept,
        hosts: inv.hosts || inv.title,
        extra_prompt: `${concept?.vibe || ""}. ${aiPrompt || ""}`.trim(),
        reference_images: aiRefs.filter(Boolean),
      });
      update({ generated_bg_url: res.image_data_url });
      toast.success("¡Arte generado!");
      if (autoTheme) await applyThemeFromImage(res.image_data_url, { silent: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar la imagen");
    } finally {
      setGenLoading(false);
    }
  };

  const handleSave = async () => {
    if (!inv) return;
    if (!inv.event_date) {
      toast.error("Elige la fecha del evento");
      return;
    }
    try {
      setSaving(true);
      let result;
      if (inv.id) {
        result = await updateInvitation(inv.id, inv);
      } else {
        result = await createInvitation(inv);
      }
      setInv(result);
      toast.success("¡Invitación guardada!");
      navigate(`/i/${result.slug}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const copyLink = () => {
    if (!inv?.slug) return;
    const url = `${window.location.origin}/i/${inv.slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copiado");
  };

  const copyTestLink = () => {
    if (!inv?.preview_token) { toast.error("Guarda primero para generar el link de prueba"); return; }
    const url = `${window.location.origin}/preview/${inv.preview_token}`;
    navigator.clipboard.writeText(url);
    toast.success("Link de prueba copiado — compártelo con tu cliente para revisar");
  };

  if (loading || !inv) {
    return (
      <div className="min-h-screen grid place-items-center bg-cream">
        <Loader2 className="animate-spin text-warmgold" size={28} />
      </div>
    );
  }

  const theme = getTheme(inv.theme);
  const bg = resolveImageUrl(inv.generated_bg_url || inv.hero_image_url);
  const styleVars = buildStyleVars(inv.custom_style);
  // Editor-page appearance overrides (local preference). Re-map the base theme channels so the
  // whole editor chrome follows them, and detect a dark background to auto-outline text.
  const edVars = {};
  if (edUi.page_bg) edVars["--c-cream"] = rgbChannels(edUi.page_bg);
  if (edUi.text) { edVars["--c-ink"] = rgbChannels(edUi.text); edVars["--c-inksoft"] = rgbChannels(edUi.text); }
  if (edUi.section_bg) edVars["--ed-section-bg"] = edUi.section_bg;
  if (edUi.subcard_bg) edVars["--ed-subcard-bg"] = edUi.subcard_bg;
  if (edUi.btn_text) edVars["--ed-btn-text"] = edUi.btn_text;
  if (edUi.title_color) edVars["--ed-title"] = edUi.title_color;
  const effPageBg = edUi.page_bg || inv.custom_style?.bg_color || "#FBF7F0";
  edVars["--ed-text-stroke"] = edUi.text_stroke || effPageBg;
  const edScale = Number(edUi.scale ?? 1) || 1;
  const _rgb = hexToRgb(effPageBg);
  const _lum = _rgb ? (0.2126 * _rgb.r + 0.7152 * _rgb.g + 0.0722 * _rgb.b) / 255 : 1;
  const isDarkBg = _lum < 0.5;
  const autoContrast = edUi.auto_contrast !== false && isDarkBg;
  // El contorno del texto SOLO aplica en temas oscuros y lo controla el interruptor
  // "Contorno automático en temas oscuros". Si está apagado, no hay contorno aunque se haya
  // elegido un color. En temas claros está siempre desactivado. El color manual (si existe)
  // solo define el color del contorno cuando se muestra.
  const showStroke = autoContrast;
  // Context handed to the AI so stickers match THIS event (and its audience), not generic kids art
  const stickerContext = {
    concept: getConcept(inv.theme, inv.concept)?.name || inv.concept || "",
    title: inv.title || "",
    subtitle: inv.subtitle || "",
    hosts: inv.hosts || "",
    extra_context: [theme?.name, inv.dresscode?.style, inv.message, aiPrompt].filter(Boolean).join(". ").slice(0, 400),
    colors: [inv.custom_style?.accent_color, inv.custom_style?.text_color, ...(inv.dresscode?.colors || [])].filter(Boolean),
  };

  return (
    <div className={`min-h-screen bg-cream fn-scope fn-ed-scope ${showStroke ? "fn-ed-stroke" : ""} ${edUi.subcard_bg ? "fn-ed-subbg" : ""} ${edUi.btn_text ? "fn-ed-btntext" : ""}`} style={{ ...styleVars, ...edVars, "--fn-body-scale": 1 }}>
      <div className="border-b border-warmgold/20 sticky top-0 z-40 bg-cream/90 backdrop-blur-md">
        <div className="mx-auto max-w-7xl px-4 md:px-8 py-4 flex items-center justify-between gap-3 flex-wrap">
          <button onClick={() => navigate(-1)} className="btn-ghost text-sm" data-testid="editor-back-btn">
            <ArrowLeft size={16} /> Atrás
          </button>
          <div className="hidden sm:block text-center">
            <p className="script text-warmgold text-lg leading-none">{theme?.name}</p>
            <p className="text-xs text-inksoft">{inv.id ? "Editar" : "Nueva invitación"}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {/* Compact activate/deactivate switch */}
            <label className="hidden md:inline-flex items-center gap-2 cursor-pointer select-none px-3 py-2 rounded-full border border-warmgold/40 bg-white/50 hover:bg-white transition-colors" data-testid="header-active-toggle-label" title="Activar o desactivar el link de la invitación">
              <input
                type="checkbox"
                checked={inv.is_active !== false}
                onChange={(e) => handleActiveToggle(e.target.checked)}
                className="sr-only peer"
                data-testid="header-active-toggle"
              />
              <span className="relative inline-block w-9 h-5 bg-inksoft/25 peer-checked:bg-warmgold rounded-full transition-colors">
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${inv.is_active !== false ? "translate-x-4" : ""}`} />
              </span>
              <span className={`text-xs uppercase tracking-widest font-medium ${inv.is_active !== false ? "text-warmgold" : "text-inksoft"}`}>
                {inv.is_active !== false ? "Activa" : "Inactiva"}
              </span>
            </label>
            {inv.is_active === false ? (
              inv.preview_token && (
                <>
                  <a href={`/preview/${inv.preview_token}`} target="_blank" rel="noopener noreferrer" className="btn-ghost text-sm" data-testid="editor-preview-test-btn">
                    <Eye size={16} /> Ver prueba
                  </a>
                  <button onClick={copyTestLink} className="btn-ghost text-sm border border-warmgold/40" data-testid="editor-copy-test-btn">
                    <Copy size={16} /> Copiar link de prueba
                  </button>
                </>
              )
            ) : (
              inv.slug && (
                <>
                  <Link to={`/i/${inv.slug}`} className="btn-ghost text-sm" data-testid="editor-preview-btn">
                    <Eye size={16} /> Ver
                  </Link>
                  <button onClick={copyLink} className="btn-ghost text-sm" data-testid="editor-copy-btn">
                    <Copy size={16} /> Copiar link
                  </button>
                </>
              )
            )}
            <button onClick={handleSave} disabled={saving} className="btn-primary text-sm py-2.5 px-5 disabled:opacity-60" data-testid="editor-save-btn">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Guardar
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 md:px-8 py-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 flex flex-col gap-4" style={{ zoom: edScale }}>
          {/* BASIC */}
          <Section defaultOpen order={1}>
            <p className="overline">Detalles del evento</p>
            <h2 className="headline text-3xl text-ink mt-2">Empecemos por lo esencial</h2>
            <div className="mt-6 rounded-2xl border border-warmgold/30 bg-white/50 p-5">
              <p className="text-xs uppercase tracking-widest text-inksoft">Temática del evento</p>
              <p className="text-xs text-inksoft/80 mt-1">
                Cambia la temática cuando quieras: los textos automáticos (como el del contador) se adaptan. Usa <strong>Otros</strong> para reuniones, aniversarios, graduaciones y textos neutros.
              </p>
              <div className="mt-4 flex flex-wrap gap-2" data-testid="theme-switcher">
                {THEME_LIST.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => handleThemeChange(t.id)}
                    data-testid={`theme-option-${t.id}`}
                    className={`text-sm rounded-full px-4 py-2 border transition-colors ${
                      inv.theme === t.id
                        ? "bg-warmgold text-cream border-warmgold"
                        : "bg-white/70 text-ink border-warmgold/40 hover:bg-white"
                    }`}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
              {theme && (
                <p className="text-xs text-inksoft mt-3" data-testid="theme-countdown-hint">
                  Texto del contador: “{inv.countdown_label || theme.countdownLabel}”
                </p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <Field
                  label="Texto del contador (opcional)"
                  value={inv.countdown_label}
                  onChange={(v) => update({ countdown_label: v })}
                  placeholder={theme?.countdownLabel || "Faltan para el evento"}
                  testid="field-countdown-label"
                />
                <Field
                  label="Etiqueta del hero (opcional)"
                  value={inv.theme_label}
                  onChange={(v) => update({ theme_label: v })}
                  placeholder={theme?.tagline || "Una celebración especial"}
                  testid="field-theme-label"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <Field label="Título" value={inv.title} onChange={(v) => update({ title: v })} testid="field-title" />
              <Field label="Anfitriones" value={inv.hosts} onChange={(v) => update({ hosts: v })} testid="field-hosts" />
              <Field label="Subtítulo" value={inv.subtitle} onChange={(v) => update({ subtitle: v })} testid="field-subtitle" full />
              <Field label="Fecha" type="date" value={inv.event_date} onChange={(v) => update({ event_date: v })} testid="field-date" />
              <Field label="Hora" type="time" value={inv.event_time} onChange={(v) => update({ event_time: v })} testid="field-time" />
              <Field label="Duración aprox. (horas)" type="number" value={inv.event_duration_hours ?? 5} onChange={(v) => update({ event_duration_hours: Math.max(0.5, Math.min(24, parseFloat(v) || 0)) })} placeholder="5" testid="field-duration" />
              <Field label="Nombre del lugar" value={inv.venue_name} onChange={(v) => update({ venue_name: v })} testid="field-venue" full />
              <Field label="Dirección" value={inv.address} onChange={(v) => update({ address: v })} testid="field-address" full />
              <Field label="Google Maps URL" value={inv.gmaps_url} onChange={(v) => update({ gmaps_url: v })} testid="field-gmaps" full />
              <Field label="WhatsApp del anfitrión" value={inv.host_whatsapp} onChange={(v) => update({ host_whatsapp: v })} testid="field-whatsapp" full />
              <Field label="Hashtag oficial" value={inv.hashtag} onChange={(v) => update({ hashtag: v })} placeholder="#BodaAnaYLuis" testid="field-hashtag" full />
            </div>
          </Section>

          {/* CEREMONY / MISA */}
          <Section order={6}>
            <p className="overline">Misa o ceremonia (opcional)</p>
            <p className="text-xs text-inksoft mt-1">Si hay misa antes de la fiesta, agrega su horario y dirección; se mostrará como una segunda ubicación con su mapa.</p>
            <label className="mt-3 inline-flex items-center gap-3 cursor-pointer select-none rounded-full border border-warmgold/40 bg-white/50 px-4 py-2 hover:bg-white transition-colors" data-testid="toggle-ceremony-label">
              <input
                type="checkbox"
                checked={!!inv.ceremony_enabled}
                onChange={(e) => update({ ceremony_enabled: e.target.checked })}
                className="sr-only peer"
                data-testid="toggle-ceremony"
              />
              <span className="relative inline-block w-9 h-5 bg-inksoft/25 peer-checked:bg-warmgold rounded-full transition-colors">
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${inv.ceremony_enabled ? "translate-x-4" : ""}`} />
              </span>
              <span className="text-xs uppercase tracking-widest font-medium text-ink">Habrá misa / ceremonia</span>
            </label>
            {inv.ceremony_enabled && (
              <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <Field label="Cómo se llama" value={inv.ceremony_label || ""} onChange={(v) => update({ ceremony_label: v })} placeholder="Misa" testid="field-ceremony-label" />
                <Field label="Hora de la misa" type="time" value={inv.ceremony_time || ""} onChange={(v) => update({ ceremony_time: v })} testid="field-ceremony-time" />
                <Field label="Nombre del templo / lugar" value={inv.ceremony_venue_name || ""} onChange={(v) => update({ ceremony_venue_name: v })} placeholder="Parroquia de San José" testid="field-ceremony-venue" full />
                <Field label="Dirección de la misa" value={inv.ceremony_address || ""} onChange={(v) => update({ ceremony_address: v })} testid="field-ceremony-address" full />
                <Field label="Google Maps URL (misa)" value={inv.ceremony_gmaps_url || ""} onChange={(v) => update({ ceremony_gmaps_url: v })} testid="field-ceremony-gmaps" full />
              </div>
              <div className="mt-6" data-testid="countdown-target-group">
                <span className="text-xs tracking-widest uppercase text-inksoft">¿Qué hora usa la cuenta regresiva?</span>
                <p className="text-[11px] text-inksoft mt-1">Si tu evento tiene misa y fiesta, elige a qué hora cuenta el contador.</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {[
                    { v: "party", label: "Hora de la fiesta" },
                    { v: "ceremony", label: `Hora de ${inv.ceremony_label || "la misa"}` },
                  ].map((o) => {
                    const active = (inv.countdown_target || "party") === o.v;
                    return (
                      <button
                        key={o.v}
                        type="button"
                        onClick={() => update({ countdown_target: o.v })}
                        data-testid={`countdown-target-${o.v}`}
                        className={`text-xs rounded-full px-4 py-2 border transition-colors ${
                          active ? "bg-warmgold text-cream border-warmgold" : "bg-white/70 text-ink border-warmgold/40 hover:bg-white"
                        }`}
                      >
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              </>
            )}
          </Section>

          {/* PRESENTATION IMAGES: hero + global parallax */}
          <Section order={3}>
            <p className="overline">Imágenes de presentación</p>
            <div className="mt-5">
            <p className="text-xs tracking-widest uppercase text-inksoft mb-2">Imagen principal</p>
            <p className="text-xs text-inksoft mt-1">Sube tu propia imagen (reemplaza la de la IA) o deja la del concepto elegido.</p>
            <div className="mt-4">
              <ImageUpload value={inv.hero_image_url} onChange={handleHeroChange} label="Fondo hero" aspect="aspect-[3/4] max-w-xs" testid="hero-upload" />
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <label className="inline-flex items-center gap-3 cursor-pointer select-none rounded-full border border-warmgold/40 bg-white/50 px-4 py-2 hover:bg-white transition-colors" data-testid="auto-theme-toggle-label">
                <input type="checkbox" checked={autoTheme} onChange={(e) => setAutoTheme(e.target.checked)} className="sr-only peer" data-testid="auto-theme-toggle" />
                <span className="relative inline-block w-9 h-5 bg-inksoft/25 peer-checked:bg-warmgold rounded-full transition-colors">
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${autoTheme ? "translate-x-4" : ""}`} />
                </span>
                <span className="text-xs uppercase tracking-widest font-medium text-ink">Tomar colores de la imagen</span>
              </label>
              {(inv.hero_image_url || inv.generated_bg_url) && (
                <button
                  type="button"
                  onClick={() => applyThemeFromImage(inv.generated_bg_url || inv.hero_image_url)}
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[11px] uppercase tracking-widest font-medium bg-warmgold/10 text-warmgold border border-warmgold/40 hover:bg-warmgold/20 transition-colors"
                  data-testid="reapply-theme-btn"
                >
                  <Sparkles size={12} /> Volver a tomar colores
                </button>
              )}
            </div>
            </div>

            <div className="mt-8 pt-6 border-t border-warmgold/15">
            <p className="text-xs tracking-widest uppercase text-inksoft mb-2">Fondo global con parallax</p>
            <div className="mt-4">
              <GlobalBackground
                imageUrl={inv.global_bg_url}
                opacity={inv.global_bg_opacity}
                onChange={(patch) => update(patch)}
              />
            </div>
            </div>
          </Section>

          {/* MESSAGES & POSTDATA */}
          <Section order={5}>
            <p className="overline">Mensajes y post data</p>
            <div className="mt-5">
            <p className="text-xs tracking-widest uppercase text-inksoft mb-2">Mensaje personal</p>
            <textarea data-testid="field-message" value={inv.message} onChange={(e) => update({ message: e.target.value })} rows={3} className="w-full bg-transparent border-b border-warmgold/40 focus:border-warmgold focus:outline-none py-2 text-ink resize-none" />
            <label className="mt-3 inline-flex items-center gap-3 cursor-pointer select-none rounded-full border border-warmgold/40 bg-white/50 px-4 py-2 hover:bg-white transition-colors" data-testid="toggle-message-quotes-label">
              <input
                type="checkbox"
                checked={inv.message_show_quotes !== false}
                onChange={(e) => update({ message_show_quotes: e.target.checked })}
                className="sr-only peer"
                data-testid="toggle-message-quotes"
              />
              <span className="relative inline-block w-9 h-5 bg-inksoft/25 peer-checked:bg-warmgold rounded-full transition-colors">
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${inv.message_show_quotes !== false ? "translate-x-4" : ""}`} />
              </span>
              <span className="text-xs uppercase tracking-widest font-medium text-ink">Mostrar comillas “ ”</span>
            </label>
            </div>

            <div className="mt-8 pt-6 border-t border-warmgold/15">
            <p className="text-xs tracking-widest uppercase text-inksoft mb-2">Mensaje de invitación</p>
            <p className="text-xs text-inksoft mt-1">Un texto libre que aparece en la invitación <strong>sin título</strong>. Puedes moverlo y colocarlo donde quieras desde “Orden de las secciones”.</p>
            <textarea data-testid="field-invitation-message" value={inv.invitation_message || ""} onChange={(e) => update({ invitation_message: e.target.value })} rows={3} placeholder="Con mucho cariño te invitamos a acompañarnos en este día tan especial…" className="mt-3 w-full bg-transparent border-b border-warmgold/40 focus:border-warmgold focus:outline-none py-2 text-ink placeholder:text-inksoft/50 resize-none" />
            {inv.hosts && (
              <div className="mt-4" data-testid="signature-block-group">
                <span className="text-xs tracking-widest uppercase text-inksoft">¿Dónde va la firma “— {inv.hosts}”?</span>
                <p className="text-[11px] text-inksoft mt-1">Elige debajo de qué mensaje aparece el nombre de los anfitriones.</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {[
                    { v: "message", label: "Mensaje personal" },
                    { v: "invitation_message", label: "Mensaje de invitación" },
                    { v: "none", label: "No mostrar" },
                  ].map((o) => {
                    const active = (inv.signature_block || "message") === o.v;
                    return (
                      <button
                        key={o.v}
                        type="button"
                        onClick={() => update({ signature_block: o.v })}
                        data-testid={`signature-block-${o.v}`}
                        className={`text-xs rounded-full px-4 py-2 border transition-colors ${
                          active ? "bg-warmgold text-cream border-warmgold" : "bg-white/70 text-ink border-warmgold/40 hover:bg-white"
                        }`}
                      >
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            </div>

            <div className="mt-8 pt-6 border-t border-warmgold/15">
            <p className="text-xs tracking-widest uppercase text-inksoft mb-2">P.D. (postdata)</p>
            <p className="text-xs text-inksoft mt-1">Información extra al final de la invitación. Ej: “P.D. No traigan regalos, solo buena vibra” o “P.D. Habrá karaoke, prepara tu canción”.</p>
            <textarea
              data-testid="field-postdata"
              value={inv.postdata || ""}
              onChange={(e) => update({ postdata: e.target.value })}
              rows={3}
              placeholder="P.D. ..."
              className="mt-3 w-full bg-transparent border-b border-warmgold/40 focus:border-warmgold focus:outline-none py-2 text-ink placeholder:text-inksoft/50 resize-none"
            />
            </div>
          </Section>

          {/* MUSIC */}
          <Section order={11}>
            <p className="overline">Música de fondo</p>
            <p className="text-xs text-inksoft mt-1 leading-relaxed">
              Sube un MP3 para autoplay 100% garantizado (recomendado), o pega un link de Spotify. En la invitación solo aparece un botón flotante para silenciar/activar.
            </p>

            <div className="mt-4">
              <span className="text-xs tracking-widest uppercase text-inksoft">Archivo de audio (recomendado)</span>
              <div className="mt-2">
                <AudioUpload value={inv.music.audio_url} onChange={(v) => updateMusic({ audio_url: v, provider: v ? "audio" : inv.music.provider })} />
              </div>
            </div>

            <div className="mt-6">
              <p className="text-[11px] uppercase tracking-widest text-inksoft mb-3">— O usa un link (opcional) —</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <label className="block">
                  <span className="text-xs tracking-widest uppercase text-inksoft">Plataforma</span>
                  <select
                    data-testid="field-music-provider"
                    value={inv.music.provider === "audio" ? "" : inv.music.provider}
                    onChange={(e) => updateMusic({ provider: e.target.value })}
                    className="mt-2 w-full bg-transparent border-b border-warmgold/40 focus:border-warmgold focus:outline-none py-2 text-ink"
                  >
                    <option value="">— Sin link —</option>
                    <option value="spotify">Spotify</option>
                    <option value="apple">Apple Music</option>
                  </select>
                </label>
                <div className="md:col-span-2">
                  <Field label="URL del track/playlist" value={inv.music.url} onChange={(v) => updateMusic({ url: v })} placeholder={inv.music.provider === "apple" ? "https://music.apple.com/..." : "https://open.spotify.com/track/..."} testid="field-music-url" />
                </div>
              </div>
              <p className="text-[10px] text-inksoft mt-2 leading-relaxed">Nota: Spotify puede bloquear autoplay en algunos navegadores. Si es crítico que suene al abrir, sube el MP3.</p>
            </div>
          </Section>

          {/* DRESSCODE */}
          <Section order={8}>
            <p className="overline">Dress code</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-3">
              <Field label="Estilo" value={inv.dresscode.style} onChange={(v) => updateDress({ style: v })} testid="field-dress-style" />
              <label className="block md:col-span-2">
                <span className="text-xs tracking-widest uppercase text-inksoft">Nota</span>
                <textarea
                  value={inv.dresscode.note || ""}
                  onChange={(e) => updateDress({ note: e.target.value })}
                  rows={4}
                  placeholder={"Ven con tu mejor look playero.\n\n🚫 Prohibido venir de NEGRO"}
                  data-testid="field-dress-note"
                  className="mt-2 w-full bg-transparent border-b border-warmgold/40 focus:border-warmgold focus:outline-none py-2 text-ink placeholder:text-inksoft/50 resize-y whitespace-pre-line"
                />
                <span className="block text-[11px] text-inksoft mt-1">Usa Enter para separar líneas; se respetan tal cual en la invitación.</span>
              </label>
              <Field label="Tablero de Pinterest (URL)" value={inv.dresscode.pinterest_board_url} onChange={(v) => updateDress({ pinterest_board_url: v })} placeholder="https://pinterest.com/user/board/" testid="field-dress-pinterest" full />
            </div>
            <div className="mt-4">
              <StickerButton
                text={`dress code ${inv.dresscode.style || ""} ${inv.dresscode.note || ""}`}
                theme={inv.theme}
                context={stickerContext}
                value={inv.dresscode.sticker_url}
                onChange={(url) => updateDress({ sticker_url: url })}
                testid="dress-sticker-btn"
              />
            </div>
            <div className="mt-5">
              <span className="text-xs tracking-widest uppercase text-inksoft">Paleta</span>
              <p className="text-[10px] text-inksoft mt-0.5">Elige con el círculo o escribe el HEX (#RRGGBB).</p>
              <div className="mt-3 space-y-2">
                {(inv.dresscode.colors || []).map((c, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <input
                      type="color"
                      value={/^#[0-9a-fA-F]{6}$/.test(c) ? c : "#C5A880"}
                      onChange={(e) => {
                        const arr = [...inv.dresscode.colors];
                        arr[i] = e.target.value;
                        updateDress({ colors: arr });
                      }}
                      className="w-11 h-11 rounded-full border-2 border-white shadow cursor-pointer shrink-0"
                      data-testid={`dress-color-${i}`}
                    />
                    <input
                      type="text"
                      value={c || ""}
                      onChange={(e) => {
                        const arr = [...inv.dresscode.colors];
                        arr[i] = e.target.value.toUpperCase();
                        updateDress({ colors: arr });
                      }}
                      placeholder="#C5A880"
                      className="flex-1 max-w-[140px] font-mono uppercase text-sm bg-transparent border-b border-warmgold/40 focus:border-warmgold focus:outline-none py-2 text-ink"
                      data-testid={`dress-color-hex-${i}`}
                    />
                    <button
                      onClick={() => updateDress({ colors: inv.dresscode.colors.filter((_, idx) => idx !== i) })}
                      className="text-inksoft hover:text-red-500 p-1"
                      data-testid={`dress-color-remove-${i}`}
                      aria-label="Quitar color"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                {(inv.dresscode.colors?.length || 0) < 6 && (
                  <button onClick={() => updateDress({ colors: [...(inv.dresscode.colors || []), "#C5A880"] })} className="btn-ghost text-sm" data-testid="dress-color-add">
                    <Plus size={14} /> Añadir color
                  </button>
                )}
              </div>
            </div>
          </Section>

          {/* ITINERARY */}
          <Section order={7}>
            <p className="overline">Itinerario</p>
            <label className="mt-3 inline-flex items-center gap-3 cursor-pointer select-none rounded-full border border-warmgold/40 bg-white/50 px-4 py-2 hover:bg-white transition-colors" data-testid="toggle-itinerary-times-label">
              <input
                type="checkbox"
                checked={inv.itinerary_show_times !== false}
                onChange={(e) => update({ itinerary_show_times: e.target.checked })}
                className="sr-only peer"
                data-testid="toggle-itinerary-times"
              />
              <span className="relative inline-block w-9 h-5 bg-inksoft/25 peer-checked:bg-warmgold rounded-full transition-colors">
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${inv.itinerary_show_times !== false ? "translate-x-4" : ""}`} />
              </span>
              <span className="text-xs uppercase tracking-widest font-medium text-ink">Mostrar horarios</span>
            </label>
            <p className="text-[11px] text-inksoft mt-2">Apágalo si solo quieres una lista de lo que harán (sin horas): “Comer rico”, “Tomar fotitos”, “Bailar hasta que el cuerpo aguante”…</p>
            <div className="mt-3 space-y-3">
              {(inv.itinerary || []).map((it, i) => (
                <div key={i} className="glass rounded-2xl p-4 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-8 gap-3 items-end">
                    {inv.itinerary_show_times !== false && (
                      <input placeholder="19:00" value={it.time || ""} onChange={(e) => { const arr = [...inv.itinerary]; arr[i] = { ...arr[i], time: e.target.value }; update({ itinerary: arr }); }} className="md:col-span-1 bg-transparent border-b border-warmgold/40 focus:border-warmgold focus:outline-none py-2 text-ink" data-testid={`itin-time-${i}`} />
                    )}
                    <input placeholder={inv.itinerary_show_times !== false ? "Ceremonia" : "Comer rico"} value={it.title || ""} onChange={(e) => { const arr = [...inv.itinerary]; arr[i] = { ...arr[i], title: e.target.value }; update({ itinerary: arr }); }} className={`${inv.itinerary_show_times !== false ? "md:col-span-3" : "md:col-span-4"} bg-transparent border-b border-warmgold/40 focus:border-warmgold focus:outline-none py-2 text-ink`} data-testid={`itin-title-${i}`} />
                    <input placeholder="Detalle (opcional)" value={it.description || ""} onChange={(e) => { const arr = [...inv.itinerary]; arr[i] = { ...arr[i], description: e.target.value }; update({ itinerary: arr }); }} className="md:col-span-3 bg-transparent border-b border-warmgold/40 focus:border-warmgold focus:outline-none py-2 text-ink" data-testid={`itin-desc-${i}`} />
                    <div className="md:col-span-1 flex items-center justify-end gap-1">
                      <button
                        onClick={() => { if (i === 0) return; const arr = [...inv.itinerary]; [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]]; update({ itinerary: arr }); }}
                        disabled={i === 0}
                        className="p-1.5 rounded-full text-inksoft hover:text-warmgold hover:bg-warmgold/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        data-testid={`itin-up-${i}`}
                        aria-label="Subir momento"
                        title="Subir"
                      ><ChevronUp size={16} /></button>
                      <button
                        onClick={() => { if (i === inv.itinerary.length - 1) return; const arr = [...inv.itinerary]; [arr[i + 1], arr[i]] = [arr[i], arr[i + 1]]; update({ itinerary: arr }); }}
                        disabled={i === (inv.itinerary || []).length - 1}
                        className="p-1.5 rounded-full text-inksoft hover:text-warmgold hover:bg-warmgold/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        data-testid={`itin-down-${i}`}
                        aria-label="Bajar momento"
                        title="Bajar"
                      ><ChevronDown size={16} /></button>
                      <button onClick={() => update({ itinerary: inv.itinerary.filter((_, idx) => idx !== i) })} className="p-1.5 rounded-full text-inksoft hover:text-red-600 hover:bg-red-50 transition-colors" data-testid={`itin-remove-${i}`} aria-label="Eliminar momento"><Trash2 size={16} /></button>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <StickerButton
                      text={`${it.title || ""} ${it.description || ""}`}
                      theme={inv.theme}
                context={stickerContext}
                      value={it.sticker_url}
                      onChange={(url) => { const arr = [...inv.itinerary]; arr[i] = { ...arr[i], sticker_url: url }; update({ itinerary: arr }); }}
                      testid={`itin-sticker-btn-${i}`}
                    />
                  </div>
                </div>
              ))}
              <button onClick={() => update({ itinerary: [...(inv.itinerary || []), { time: "", title: "", description: "" }] })} className="btn-ghost text-sm" data-testid="itin-add">
                <Plus size={14} /> Agregar momento
              </button>
            </div>
          </Section>

          {/* PADRINOS */}
          <Section order={9}>
            <p className="overline">Padrinos / Damas y chambelanes</p>
            <div className="mt-3 space-y-3">
              {(inv.padrinos || []).map((p, i) => (
                <div key={i} className="glass rounded-2xl p-4 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-8 gap-3 items-end">
                    <input placeholder="Rol (Padrinos de velación)" value={p.role || ""} onChange={(e) => { const arr = [...inv.padrinos]; arr[i] = { ...arr[i], role: e.target.value }; update({ padrinos: arr }); }} className="md:col-span-3 bg-transparent border-b border-warmgold/40 focus:border-warmgold focus:outline-none py-2 text-ink" data-testid={`padr-role-${i}`} />
                    <input placeholder="Nombres" value={p.names || ""} onChange={(e) => { const arr = [...inv.padrinos]; arr[i] = { ...arr[i], names: e.target.value }; update({ padrinos: arr }); }} className="md:col-span-4 bg-transparent border-b border-warmgold/40 focus:border-warmgold focus:outline-none py-2 text-ink" data-testid={`padr-names-${i}`} />
                    <button onClick={() => update({ padrinos: inv.padrinos.filter((_, idx) => idx !== i) })} className="text-inksoft hover:text-ink"><Trash2 size={16} /></button>
                  </div>
                  <StickerButton
                    text={`${p.role || ""} ${p.names || ""}`}
                    theme={inv.theme}
                context={stickerContext}
                    value={p.sticker_url}
                    onChange={(url) => { const arr = [...inv.padrinos]; arr[i] = { ...arr[i], sticker_url: url }; update({ padrinos: arr }); }}
                    testid={`padr-sticker-btn-${i}`}
                  />
                </div>
              ))}
              <button onClick={() => update({ padrinos: [...(inv.padrinos || []), { role: "", names: "" }] })} className="btn-ghost text-sm" data-testid="padr-add">
                <Plus size={14} /> Agregar
              </button>
            </div>
          </Section>

          {/* EXTRA INFO */}
          <Section order={13}>
            <p className="overline">Info extra</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
              <TextField label="Hospedaje / Recomendaciones" value={inv.lodging} onChange={(v) => update({ lodging: v })} testid="field-lodging" />
              <TextField label="Kids policy" value={inv.kids_policy} onChange={(v) => update({ kids_policy: v })} testid="field-kids" />
              <TextField label="Estacionamiento" value={inv.parking_info} onChange={(v) => update({ parking_info: v })} testid="field-parking" />
              <Field label="Live streaming URL" value={inv.live_stream_url} onChange={(v) => update({ live_stream_url: v })} placeholder="https://youtube.com/live/..." testid="field-livestream" />
            </div>

            <div className="mt-6" data-testid="extra-fields-editor">
              <p className="text-xs tracking-widest uppercase text-inksoft">Campos personalizados</p>
              <p className="text-xs text-inksoft/80 mt-1">Agrega los que quieras con tu propio título y descripción (por ejemplo “Transporte”, “Mesa de dulces”, “Reglas de la alberca”).</p>
              <div className="mt-3 space-y-3">
                {(inv.extra_fields || []).map((f, i) => (
                  <div key={i} className="glass rounded-2xl p-4 space-y-3" data-testid={`extra-field-${i}`}>
                    <div className="flex items-start gap-3">
                      <input
                        placeholder="Título del campo"
                        value={f.title || ""}
                        onChange={(e) => { const arr = [...(inv.extra_fields || [])]; arr[i] = { ...arr[i], title: e.target.value }; update({ extra_fields: arr }); }}
                        className="flex-1 bg-transparent border-b border-warmgold/40 focus:border-warmgold focus:outline-none py-2 text-ink"
                        data-testid={`extra-field-title-${i}`}
                      />
                      <button
                        onClick={() => update({ extra_fields: (inv.extra_fields || []).filter((_, idx) => idx !== i) })}
                        className="text-inksoft hover:text-ink mt-2"
                        data-testid={`extra-field-remove-${i}`}
                        aria-label="Quitar campo"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <textarea
                      placeholder="Descripción"
                      rows={3}
                      value={f.text || ""}
                      onChange={(e) => { const arr = [...(inv.extra_fields || [])]; arr[i] = { ...arr[i], text: e.target.value }; update({ extra_fields: arr }); }}
                      className="w-full rounded-xl bg-white/60 border border-warmgold/25 focus:border-warmgold focus:outline-none px-3 py-2 text-sm text-ink placeholder:text-inksoft/50 resize-none"
                      data-testid={`extra-field-text-${i}`}
                    />
                  </div>
                ))}
                <button
                  onClick={() => update({ extra_fields: [...(inv.extra_fields || []), { title: "", text: "" }] })}
                  className="btn-ghost text-sm"
                  data-testid="extra-field-add"
                >
                  <Plus size={14} /> Agregar campo
                </button>
              </div>
            </div>

          </Section>

          {/* COVER SETTINGS: glow + positions + hero badges */}
          <Section order={2}>
            <p className="overline">Ajustes de portada</p>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <RangeField
                label="Difuminado detrás de los textos de la portada"
                value={inv.hero_glow_opacity ?? 1}
                min={0} max={1} step={0.05}
                format={(v) => `${Math.round(v * 100)}%`}
                hint="0% deja ver la portada tal cual; 100% es el difuminado original (más legible)."
                onChange={(v) => update({ hero_glow_opacity: v })}
                testid="range-hero-glow"
              />
              {inv.hero_glow_tone !== "none" && (
                <>
                  <RangeField
                    label="Ancho del difuminado"
                    value={inv.hero_glow_width ?? 1}
                    min={0.01} max={1.5} step={0.01}
                    format={(v) => `${Math.round(v * 100)}%`}
                    hint="Qué tan ancho es el difuminado detrás de los textos de la portada."
                    onChange={(v) => update({ hero_glow_width: v })}
                    testid="range-hero-glow-width"
                  />
                  <RangeField
                    label="Alto del difuminado"
                    value={inv.hero_glow_height ?? 1}
                    min={0.01} max={1.5} step={0.01}
                    format={(v) => `${Math.round(v * 100)}%`}
                    hint="Qué tan alto es el difuminado detrás de los textos de la portada."
                    onChange={(v) => update({ hero_glow_height: v })}
                    testid="range-hero-glow-height"
                  />
                </>
              )}
              <div>
                <span className="text-xs tracking-widest uppercase text-inksoft">Color del difuminado</span>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="glow-tone-group">
                  {[
                    { id: "auto", label: "Automático" },
                    { id: "light", label: "Blanco" },
                    { id: "dark", label: "Negro" },
                    { id: "none", label: "Sin difuminado" },
                    { id: "custom", label: "Personalizado" },
                  ].map((o) => {
                    const active = (inv.hero_glow_tone || "auto") === o.id;
                    return (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => update({ hero_glow_tone: o.id })}
                        data-testid={`glow-tone-${o.id}`}
                        className={`text-xs rounded-full px-4 py-2 border transition-colors ${
                          active ? "bg-warmgold text-cream border-warmgold" : "bg-white/70 text-ink border-warmgold/40 hover:bg-white"
                        }`}
                      >
                        {o.label}
                      </button>
                    );
                  })}
                </div>
                {(inv.hero_glow_tone === "custom") && (
                  <div className="mt-4 rounded-2xl border border-warmgold/30 bg-white/60 p-4" data-testid="glow-custom-panel">
                    <span className="block text-[11px] tracking-widest uppercase text-inksoft mb-3">Elige el color del difuminado</span>
                    <div className="flex flex-wrap gap-2">
                      {[
                        "#F5EFE6", "#1E1814", "#C5A880", "#E8B4B8", "#A98FC9",
                        "#7FB3D5", "#82C9A0", "#F4C06B", "#E07A5F", "#3D5A80",
                        "#9B5DE5", "#F15BB5", "#00BBF9", "#00F5D4", "#6A4C93",
                      ].map((c) => {
                        const selected = (inv.hero_glow_color || "#C5A880").toLowerCase() === c.toLowerCase();
                        return (
                          <button
                            key={c}
                            type="button"
                            onClick={() => update({ hero_glow_color: c })}
                            data-testid={`glow-swatch-${c.replace('#','')}`}
                            title={c}
                            className={`h-8 w-8 rounded-full border transition-transform hover:scale-110 ${
                              selected ? "ring-2 ring-warmgold ring-offset-2 ring-offset-white border-warmgold" : "border-black/10"
                            }`}
                            style={{ backgroundColor: c }}
                          />
                        );
                      })}
                    </div>
                    <label className="mt-4 flex items-center gap-3">
                      <span className="text-[11px] text-inksoft">Color exacto</span>
                      <input
                        type="color"
                        value={inv.hero_glow_color || "#C5A880"}
                        onChange={(e) => update({ hero_glow_color: e.target.value })}
                        data-testid="glow-color-input"
                        className="h-9 w-14 rounded-md border border-warmgold/40 bg-transparent cursor-pointer"
                      />
                      <input
                        type="text"
                        value={inv.hero_glow_color || "#C5A880"}
                        onChange={(e) => update({ hero_glow_color: e.target.value })}
                        data-testid="glow-color-hex"
                        className="w-28 bg-transparent border-b border-warmgold/40 focus:border-warmgold focus:outline-none py-1 text-sm text-ink uppercase"
                      />
                    </label>
                    <span className="block text-[11px] text-inksoft/80 mt-2">La intensidad del color sigue la barra de difuminado de arriba.</span>
                  </div>
                )}
                <span className="block text-xs text-inksoft/80 mt-1 leading-relaxed">
                  Automático elige según el color del subtítulo. Blanco aclara la foto detrás de los textos; negro la oscurece. Aplica a la etiqueta del hero y al subtítulo.
                </span>
              </div>
              <RangeField
                label="Posición: todo el bloque"
                value={inv.hero_text_offset ?? 0}
                min={-100} max={300} step={4}
                format={(v) => `${v > 0 ? "+" : ""}${v} px`}
                hint="Sube (+) o baja (−) juntos el subtítulo, el título y los anfitriones."
                onChange={(v) => update({ hero_text_offset: Math.round(v) })}
                testid="range-hero-offset"
              />
              <RangeField
                label="Posición: solo el subtítulo"
                value={inv.hero_offset_subtitle ?? 0}
                min={-250} max={250} step={2}
                format={(v) => `${v > 0 ? "+" : ""}${v} px`}
                onChange={(v) => update({ hero_offset_subtitle: Math.round(v) })}
                testid="range-offset-subtitle"
              />
              <RangeField
                label="Posición: solo el título"
                value={inv.hero_offset_title ?? 0}
                min={-250} max={250} step={2}
                format={(v) => `${v > 0 ? "+" : ""}${v} px`}
                onChange={(v) => update({ hero_offset_title: Math.round(v) })}
                testid="range-offset-title"
              />
              <RangeField
                label="Posición: solo los anfitriones"
                value={inv.hero_offset_hosts ?? 0}
                min={-250} max={250} step={2}
                format={(v) => `${v > 0 ? "+" : ""}${v} px`}
                onChange={(v) => update({ hero_offset_hosts: Math.round(v) })}
                testid="range-offset-hosts"
              />
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4">
              <ToggleField label={`Etiqueta de temática (${theme?.name || "temática"})`} value={inv.show_theme_label !== false} onChange={(v) => update({ show_theme_label: v })} testid="toggle-theme-label" />
              <ToggleField label="Cuadro “Invitación · Con cariño para ti”" value={inv.show_invite_badge !== false} onChange={(v) => update({ show_invite_badge: v })} testid="toggle-invite-badge" />
            </div>
          </Section>

          {/* GENERAL SETTINGS */}
          <Section order={15}>
            <p className="overline">Ajustes generales</p>
            <div className="mt-6 grid grid-cols-2 gap-4">
              <ToggleField label="Botones de compartir" value={inv.share_enabled !== false} onChange={(v) => update({ share_enabled: v })} testid="toggle-share" />
              <ToggleField label="Libro de recuerdos" value={inv.guestbook_enabled} onChange={(v) => update({ guestbook_enabled: v })} testid="toggle-guestbook" />
              <ToggleField label="Playlist colaborativa" value={inv.playlist_enabled} onChange={(v) => update({ playlist_enabled: v })} testid="toggle-playlist" />
            </div>
            {inv.guestbook_enabled && (
              <label className="block mt-4" data-testid="guestbook-title-field">
                <span className="text-xs tracking-widest uppercase text-inksoft">Título del libro de recuerdos</span>
                <input
                  value={inv.guestbook_title || ""}
                  onChange={(e) => update({ guestbook_title: e.target.value })}
                  placeholder="Déjanos un mensaje"
                  className="mt-2 w-full bg-transparent border-b border-warmgold/40 focus:border-warmgold focus:outline-none py-2 text-ink placeholder:text-inksoft/50"
                  data-testid="field-guestbook-title"
                />
                <span className="block text-[11px] text-inksoft mt-1">Cambia el texto grande del libro de recuerdos. Si lo dejas vacío, se mostrará “Déjanos un mensaje”.</span>
              </label>
            )}
            <div className="mt-6 max-w-md">
              <Field
                label="Desactivar automáticamente después de"
                type="datetime-local"
                value={inv.auto_deactivate_at || ""}
                onChange={(v) => update({ auto_deactivate_at: v || null })}
                placeholder=""
                testid="field-auto-deactivate"
                full
              />
              <p className="text-[11px] text-inksoft mt-2">Opcional. Si eliges una fecha y hora, la invitación se bloqueará sola cuando llegue ese momento. Déjalo en blanco para desactivarla manualmente.</p>
            </div>

            {/* Guests share — password-protected link for planners / coordinators */}
            <div className="mt-8 rounded-2xl border border-warmgold/25 bg-white/30 p-4 space-y-4" data-testid="guests-share-block">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-widest text-ink">Compartir lista de invitados</p>
                  <p className="text-[11px] text-inksoft mt-1">Genera un link privado protegido por contraseña para que un tercero (organizadora, planner) pueda ver quién confirmó y descargar el Excel.</p>
                </div>
                <ToggleField label="" value={inv.guests_share_enabled === true} onChange={(v) => update({ guests_share_enabled: v })} testid="toggle-guests-share" />
              </div>
              {inv.guests_share_enabled && (
                <>
                  <Field
                    label="Contraseña"
                    type="text"
                    value={inv.guests_share_password || ""}
                    onChange={(v) => update({ guests_share_password: v })}
                    placeholder="Elige una contraseña sencilla (ej: familia2026)"
                    testid="field-guests-password"
                    full
                  />
                  <p className="text-[11px] text-inksoft">La contraseña se guarda encriptada. Vuelve a escribirla si necesitas cambiarla.</p>
                  {inv.slug && (
                    <div className="flex items-center gap-2 rounded-xl bg-cream border border-warmgold/30 px-3 py-2">
                      <input
                        readOnly
                        value={`${window.location.origin}/g/${inv.slug}`}
                        className="flex-1 bg-transparent text-xs text-ink focus:outline-none"
                        data-testid="guests-share-url"
                        onFocus={(e) => e.target.select()}
                      />
                      <button
                        type="button"
                        onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/g/${inv.slug}`); toast.success("Link copiado"); }}
                        className="btn-ghost text-xs py-1 px-3"
                        data-testid="guests-share-copy"
                      >
                        <Copy size={12} /> Copiar
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Keepsake block — nice closing gift when the invitation is deactivated */}
            {inv.id && (
              <KeepsakePanel invitation={inv} />
            )}
          </Section>

          {/* GIFT */}
          <Section order={10}>
            <p className="overline">Regalos</p>
            <div className="mt-3 space-y-6">
              <Field label="Mensaje sobre regalos" value={inv.gift.message} onChange={(v) => updateGift({ message: v })} testid="field-gift-msg" full />
              <Field label="Datos bancarios / Sobre" value={inv.gift.bank_details} onChange={(v) => updateGift({ bank_details: v })} testid="field-gift-bank" full />
              <Field label="Mesa de regalos URL" value={inv.gift.registry_url} onChange={(v) => updateGift({ registry_url: v })} testid="field-gift-registry" full />
              <StickerButton
                text={`regalos ${inv.gift.message || "regalo de fiesta"}`}
                theme={inv.theme}
                context={stickerContext}
                value={inv.gift.sticker_url}
                onChange={(url) => updateGift({ sticker_url: url })}
                testid="gift-sticker-btn"
              />
            </div>
          </Section>

          {/* BLOCK ORDER moved to the very end (see below) */}

          {/* TYPOGRAPHY */}
          <Section order={4}>
            <div className="flex items-center gap-2">
              <TypeIcon size={16} className="text-warmgold" />
              <p className="overline">Tipografía y colores</p>
            </div>
            <p className="text-xs text-inksoft mt-1">Elige el estilo de letra y los colores. Cada elección se aplica en vivo en la vista previa.</p>
            <div className="mt-6">
              <StyleCustomizer
                style={inv.custom_style}
                onChange={updateStyle}
                heroImageUrl={inv.generated_bg_url || inv.hero_image_url}
              />
            </div>
          </Section>

          {/* GALLERY */}
          <Section order={12}>
            <p className="overline">Galería de fotos</p>
            <p className="text-xs text-inksoft mt-1">Sube hasta 6 fotos (JPG/PNG/WEBP, máx 10MB cada una). Arrastra el icono ≡ para cambiar el orden; en la invitación se acomodan solas (horizontal o vertical) sin dejar huecos.</p>
            <GalleryEditor photos={inv.photo_urls || []} onChange={(arr) => update({ photo_urls: arr })} />
          </Section>

          {/* BLOCK ORDER + VISIBILITY — placed at the very bottom so it's the last thing users configure */}
          <Section order={14}>
            <p className="overline">Orden de las secciones</p>
            <p className="text-xs text-inksoft mt-1">Arrastra para reordenar. Usa Visible/Oculta para mostrar u ocultar cada sección en la invitación pública.</p>
            <div className="mt-4">
              <BlockOrderEditor
                order={inv.block_order}
                hiddenBlocks={inv.hidden_blocks}
                onOrderChange={(next) => update({ block_order: next })}
                onHiddenChange={(next) => update({ hidden_blocks: next })}
              />
            </div>
          </Section>

          {/* EDITOR-PAGE APPEARANCE — local preference (this browser only), not saved to the invitation */}
          <Section order={16}>
            <p className="overline">Ajustes de la página de edición</p>
            <p className="text-xs text-inksoft mt-1">Personaliza cómo <strong>tú</strong> ves esta página de edición (colores y tamaño). No cambia la invitación que ven los invitados. Por defecto sigue los colores del tema elegido.</p>

            <div className="mt-4 space-y-1">
              <ColorRow label="Fondo de la página" value={edUi.page_bg} fallback={effPageBg} onChange={(v) => setEd({ page_bg: v })} onClear={() => setEd({ page_bg: "" })} testid="edui-page-bg" />
              <ColorRow label="Fondo de las secciones" value={edUi.section_bg} fallback={"#ffffff"} onChange={(v) => setEd({ section_bg: v })} onClear={() => setEd({ section_bg: "" })} testid="edui-section-bg" />
              <ColorRow label="Fondo de las subsecciones" value={edUi.subcard_bg} fallback={"#ffffff"} onChange={(v) => setEd({ subcard_bg: v })} onClear={() => setEd({ subcard_bg: "" })} testid="edui-subcard-bg" />
              <ColorRow label="Color del texto de los botones" value={edUi.btn_text} fallback={inv.custom_style?.text_color || "#2B2622"} onChange={(v) => setEd({ btn_text: v })} onClear={() => setEd({ btn_text: "" })} testid="edui-btn-text" />
              <ColorRow label="Color del texto" value={edUi.text} fallback={inv.custom_style?.text_color || "#2B2622"} onChange={(v) => setEd({ text: v })} onClear={() => setEd({ text: "" })} testid="edui-text" />
              <ColorRow label="Color del título de las secciones" value={edUi.title_color} fallback={inv.custom_style?.accent_color || "#C5A880"} onChange={(v) => setEd({ title_color: v })} onClear={() => setEd({ title_color: "" })} testid="edui-title" />
              <ColorRow label="Contorno del texto" value={edUi.text_stroke} fallback={effPageBg} onChange={(v) => setEd({ text_stroke: v })} onClear={() => setEd({ text_stroke: "" })} testid="edui-text-stroke" />
            </div>

            <div className="mt-5">
              <RangeField
                label="Tamaño del texto"
                value={edUi.scale ?? 1}
                min={0.85} max={1.3} step={0.05}
                format={(v) => `${Math.round(v * 100)}%`}
                onChange={(v) => setEd({ scale: v })}
                testid="edui-scale"
              />
            </div>

            <label className="mt-4 inline-flex items-center gap-3 cursor-pointer select-none rounded-full border border-warmgold/40 bg-white/50 px-4 py-2 hover:bg-white transition-colors" data-testid="edui-autocontrast-label">
              <input
                type="checkbox"
                checked={edUi.auto_contrast !== false}
                onChange={(e) => setEd({ auto_contrast: e.target.checked })}
                className="sr-only peer"
                data-testid="edui-autocontrast"
              />
              <span className="relative inline-block w-9 h-5 bg-inksoft/25 peer-checked:bg-warmgold rounded-full transition-colors">
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${edUi.auto_contrast !== false ? "translate-x-4" : ""}`} />
              </span>
              <span className="text-xs uppercase tracking-widest font-medium text-ink">Contorno automático en temas oscuros</span>
            </label>
            <p className="text-[11px] text-inksoft mt-2">Cuando el fondo es oscuro, se añade un contorno del color del fondo detrás del texto para que siempre se distinga.</p>

            <button
              onClick={() => setEd({ page_bg: "", section_bg: "", subcard_bg: "", btn_text: "", text: "", title_color: "", text_stroke: "", scale: 1, auto_contrast: true })}
              className="btn-ghost text-sm mt-4"
              data-testid="edui-reset"
            >
              <X size={14} /> Restablecer al tema
            </button>
          </Section>

          <div className="pt-6 border-t border-warmgold/20 space-y-3" style={{ order: 17 }}>
            <p className="text-xs text-inksoft leading-relaxed">
              Al publicar y compartir tu invitación aceptas el{" "}
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/legal/privacidad.pdf`}
                target="_blank"
                rel="noreferrer"
                className="underline underline-offset-2 text-ink hover:text-warmgold transition-colors"
                data-testid="editor-privacy-link"
              >
                Aviso de Privacidad y Términos de Uso (PDF)
              </a>
              . Las respuestas de tus invitados solo las ves tú, el contenido que publicas y con quién compartes el link
              son tu responsabilidad, y la invitación se desactiva 72 horas después del evento.
            </p>
            <div className="flex items-center justify-between">
              <p className="text-xs text-inksoft">{user?.email}</p>
              <button onClick={logout} className="btn-ghost text-sm" data-testid="editor-logout">
                <LogOut size={14} /> Cerrar sesión
              </button>
            </div>
          </div>
        </div>

        {/* PREVIEW */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto lg:pr-1">
            <p className="overline">Preview en vivo</p>
            <p className="text-xs text-inksoft mt-1 mb-4">Es la invitación real, completa y con scroll. Se ve exactamente como la verán tus invitados.</p>
            <div className="mx-auto w-[324px] max-w-full rounded-[2.5rem] p-3 bg-ink shadow-2xl">
              <div className="mx-auto rounded-[2rem] overflow-hidden bg-cream relative" style={{ width: 300, height: 640 }} data-testid="editor-live-preview">
                <IframePreview width={390} height={832} scale={300 / 390}>
                  <div className="invite-scroll" style={{ width: 390, minHeight: 832 }}>
                    <InvitationView invOverride={inv} previewMode />
                  </div>
                </IframePreview>
              </div>
            </div>

            <div className="mt-6 space-y-3">
              <label className="block">
                <span className="text-xs tracking-widest uppercase text-inksoft flex items-center gap-1"><Sparkles size={12} className="text-warmgold" /> Contexto para la IA</span>
                <textarea
                  data-testid="ai-context-input"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  rows={3}
                  placeholder="Ej: quiero un cielo estrellado, tonos lavanda, ositos abrazándose y globos en tonos pastel"
                  className="mt-2 w-full rounded-2xl bg-white/60 border border-warmgold/25 focus:border-warmgold focus:outline-none px-3 py-2 text-sm text-ink placeholder:text-inksoft/50 resize-none"
                />
              </label>

              <div data-testid="ai-reference-block">
                <span className="text-xs tracking-widest uppercase text-inksoft">Imágenes de referencia (hasta 3)</span>
                <p className="text-[10px] text-inksoft mt-1 leading-snug">La IA tomará su estilo, colores y elementos para crear tu portada.</p>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {aiRefs.map((u, i) => (
                    <div key={i} className="relative">
                      <ImageUpload
                        value={u}
                        onChange={(v) => setAiRefs((prev) => prev.map((x, idx) => (idx === i ? v : x)))}
                        label={`Ref ${i + 1}`}
                        aspect="aspect-square"
                        testid={`ai-ref-${i}`}
                      />
                      <button
                        onClick={() => setAiRefs((prev) => prev.filter((_, idx) => idx !== i))}
                        className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white shadow border border-warmgold/30 grid place-items-center text-inksoft hover:text-red-600"
                        data-testid={`ai-ref-remove-${i}`}
                        aria-label="Quitar referencia"
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                  ))}
                  {aiRefs.length < 3 && (
                    <button
                      onClick={() => setAiRefs((prev) => [...prev, ""])}
                      className="aspect-square rounded-2xl border-2 border-dashed border-warmgold/30 bg-sand/50 hover:bg-sand transition-colors grid place-items-center text-warmgold"
                      data-testid="ai-ref-add-btn"
                    >
                      <Plus size={20} />
                    </button>
                  )}
                </div>
              </div>
              <button onClick={handleGenerate} disabled={genLoading} className="btn-primary w-full disabled:opacity-60" data-testid="editor-generate-btn">
                {genLoading ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />}
                {genLoading ? "Generando arte único..." : "Generar imagen de portada con IA"}
              </button>
              <button
                onClick={handleDownloadHero}
                disabled={!(inv.generated_bg_url || inv.hero_image_url)}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium bg-white/60 text-ink border border-warmgold/50 hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="editor-download-hero-btn"
              >
                <Download size={14} className="text-warmgold" /> Descargar imagen de portada
              </button>
              <p className="text-xs text-center text-inksoft">Nano Banana crea un fondo original con tu contexto + la temática elegida.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder = "", type = "text", full = false, testid }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="text-xs tracking-widest uppercase text-inksoft">{label}</span>
      <input type={type} data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-2 w-full bg-transparent border-b border-warmgold/40 focus:border-warmgold focus:outline-none py-2 text-ink placeholder:text-inksoft/40" />
    </label>
  );
}

function Section({ children, defaultOpen = false, order }) {
  const [open, setOpen] = useState(defaultOpen);
  const arr = Children.toArray(children);
  const header = arr[0];
  const body = arr.slice(1);
  return (
    <section className="rounded-2xl border border-warmgold/20 px-4 py-3 shadow-sm" style={{ order: order != null ? order : undefined, background: "var(--ed-section-bg, rgba(255,255,255,0.4))" }}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((o) => !o); } }}
        data-testid="section-toggle"
        className="w-full flex items-center justify-between gap-3 cursor-pointer select-none"
      >
        <span className="flex-1 min-w-0">{header}</span>
        <ChevronDown size={20} className={`shrink-0 text-warmgold transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </div>
      <div className={`grid transition-all duration-300 ease-in-out ${open ? "grid-rows-[1fr] opacity-100 mt-2" : "grid-rows-[0fr] opacity-0"}`}>
        <div className="overflow-hidden min-h-0 fn-acc-body">{body}</div>
      </div>
    </section>
  );
}

function RangeField({ label, value, onChange, min, max, step = 1, format = (v) => v, hint = "", testid }) {
  const v = Number(value);
  return (
    <label className="block">
      <span className="flex items-baseline justify-between gap-3">
        <span className="text-xs tracking-widest uppercase text-inksoft">{label}</span>
        <span className="text-xs font-medium text-warmgold tabular-nums" data-testid={`${testid}-value`}>{format(v)}</span>
      </span>
      <input
        type="range"
        data-testid={testid}
        min={min}
        max={max}
        step={step}
        value={v}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="mt-3 w-full accent-warmgold cursor-pointer"
      />
      {hint && <span className="block text-xs text-inksoft/80 mt-1 leading-relaxed">{hint}</span>}
    </label>
  );
}


function TextField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs tracking-widest uppercase text-inksoft">{label}</span>
      <textarea data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={2} className="mt-2 w-full bg-transparent border-b border-warmgold/40 focus:border-warmgold focus:outline-none py-2 text-ink resize-none" />
    </label>
  );
}

function ColorRow({ label, value, fallback, onChange, onClear, testid }) {
  const v = value || fallback || "#000000";
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <span className="text-sm text-ink">{label}</span>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={v}
          onChange={(e) => onChange(e.target.value)}
          className="h-8 w-10 rounded cursor-pointer bg-transparent border border-warmgold/30 p-0"
          data-testid={testid}
        />
        {value ? (
          <button onClick={onClear} className="text-[11px] text-inksoft underline hover:text-warmgold" data-testid={`${testid}-clear`}>Tema</button>
        ) : (
          <span className="text-[11px] text-inksoft">Tema</span>
        )}
      </div>
    </div>
  );
}

function SortablePhoto({ id, index, url, onChange, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : "auto",
  };
  return (
    <div ref={setNodeRef} style={style} className="relative">
      <button
        {...attributes}
        {...listeners}
        type="button"
        className="absolute top-2 left-2 z-20 w-7 h-7 rounded-full bg-white/90 shadow border border-warmgold/30 grid place-items-center text-inksoft hover:text-warmgold cursor-grab active:cursor-grabbing touch-none"
        data-testid={`photo-drag-${index}`}
        aria-label="Arrastrar para reordenar"
        title="Arrastra para reordenar"
      >
        <GripVertical size={14} />
      </button>
      <ImageUpload value={url} onChange={onChange} label={`Foto ${index + 1}`} aspect="aspect-square" testid={`photo-${index}`} />
      <button
        type="button"
        onClick={onRemove}
        className="absolute -top-2 -right-2 z-20 w-7 h-7 rounded-full bg-white shadow border border-warmgold/30 grid place-items-center text-inksoft hover:text-red-600"
        data-testid={`photo-remove-${index}`}
      >
        <Trash2 size={12} />
      </button>
    </div>
  );
}

function GalleryEditor({ photos, onChange }) {
  const ids = photos.map((_, i) => `photo-slot-${i}`);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onChange(arrayMove(photos, oldIndex, newIndex));
  };
  return (
    <div className="mt-4" data-testid="gallery-editor">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={ids} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {photos.map((u, i) => (
              <SortablePhoto
                key={`photo-slot-${i}`}
                id={`photo-slot-${i}`}
                index={i}
                url={u}
                onChange={(v) => { const arr = [...photos]; arr[i] = v; onChange(arr); }}
                onRemove={() => onChange(photos.filter((_, idx) => idx !== i))}
              />
            ))}
            {photos.length < 6 && (
              <button
                type="button"
                onClick={() => onChange([...photos, ""])}
                className="aspect-square rounded-2xl border-2 border-dashed border-warmgold/30 bg-sand/50 hover:bg-sand transition-colors grid place-items-center text-warmgold"
                data-testid="photo-add-btn"
              >
                <Plus size={24} />
              </button>
            )}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

function ToggleField({ label, value, onChange, testid }) {
  return (
    <button onClick={() => onChange(!value)} data-testid={testid} className={`glass rounded-2xl px-4 py-4 flex items-center justify-between transition-all ${value ? "border-warmgold" : ""}`}>
      <span className="text-sm text-ink">{label}</span>
      <span className={`w-10 h-5 rounded-full relative transition-colors ${value ? "bg-warmgold" : "bg-warmgold/25"}`}>
        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${value ? "left-[22px]" : "left-0.5"}`} />
      </span>
    </button>
  );
}

function KeepsakePanel({ invitation }) {
  const inv = invitation;
  const [downloadingGuestbook, setDownloadingGuestbook] = useState(false);
  const isInactive = inv.is_active === false;

  // Whether the auto_deactivate_at date has already passed
  const autoPassed = (() => {
    if (!inv.auto_deactivate_at) return false;
    const d = new Date(inv.auto_deactivate_at);
    return !isNaN(d.getTime()) && d.getTime() <= Date.now();
  })();

  const isClosed = isInactive || autoPassed;

  const downloadGuestbookPdf = async () => {
    if (!inv.id || downloadingGuestbook) return;
    try {
      setDownloadingGuestbook(true);
      toast.loading("Preparando libro de recuerdos…", { id: "gbpdf" });
      const res = await fetch(`${API}/invitations/${inv.id}/guestbook.pdf`, { credentials: "include" });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (inv.title || "invitacion").replace(/[^A-Za-z0-9._-]+/g, "-").replace(/^-|-$/g, "") || "invitacion";
      a.download = `libro-de-recuerdos-${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success("PDF descargado", { id: "gbpdf" });
    } catch {
      toast.error("No se pudo descargar el libro", { id: "gbpdf" });
    } finally {
      setDownloadingGuestbook(false);
    }
  };

  const [downloadingInvitation, setDownloadingInvitation] = useState(false);

  const downloadInvitationPdf = async () => {
    if (!inv.id || downloadingInvitation) return;
    try {
      setDownloadingInvitation(true);
      toast.loading("Preparando invitación en PDF…", { id: "invpdf" });
      const res = await fetch(`${API}/invitations/${inv.id}/invitation.pdf`, { credentials: "include" });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (inv.title || "invitacion").replace(/[^A-Za-z0-9._-]+/g, "-").replace(/^-|-$/g, "") || "invitacion";
      a.download = `invitacion-${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success("PDF descargado", { id: "invpdf" });
    } catch {
      toast.error("No se pudo descargar la invitación", { id: "invpdf" });
    } finally {
      setDownloadingInvitation(false);
    }
  };

  return (
    <div
      className={`mt-8 rounded-2xl border p-5 space-y-4 transition-all ${
        isClosed ? "border-warmgold/60 bg-warmgold/10" : "border-warmgold/25 bg-white/30"
      }`}
      data-testid="keepsake-panel"
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-full grid place-items-center shrink-0 ${isClosed ? "bg-warmgold text-white" : "bg-warmgold/15 text-warmgold"}`}>
          <BookOpen size={18} />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold uppercase tracking-widest text-ink">
            {isClosed ? "Tu invitación quedó cerrada" : "Recuerdo de tu invitación"}
          </p>
          <p className="text-[12px] text-inksoft mt-1 leading-relaxed">
            {isClosed
              ? "Guarda estos recuerdos: el libro con todo lo que te escribieron y una copia bonita de tu invitación."
              : "Cuando tu evento termine y desactives la invitación, aquí podrás descargar tu libro de recuerdos y una copia elegante en PDF."}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={downloadGuestbookPdf}
          disabled={!inv.id || downloadingGuestbook}
          className="rounded-2xl border border-warmgold/50 bg-white px-4 py-3 flex items-center gap-3 text-left hover:bg-warmgold/5 transition-colors disabled:opacity-60"
          data-testid="keepsake-guestbook-btn"
        >
          {downloadingGuestbook ? (
            <Loader2 size={18} className="animate-spin text-warmgold shrink-0" />
          ) : (
            <BookOpen size={18} className="text-warmgold shrink-0" />
          )}
          <div className="min-w-0">
            <p className="text-sm font-medium text-ink leading-tight">Libro de recuerdos</p>
            <p className="text-[11px] text-inksoft leading-snug">Todos los mensajes en un PDF</p>
          </div>
        </button>

        <button
          type="button"
          onClick={downloadInvitationPdf}
          disabled={!inv.id || downloadingInvitation}
          className="rounded-2xl border border-warmgold/50 bg-white px-4 py-3 flex items-center gap-3 text-left hover:bg-warmgold/5 transition-colors disabled:opacity-60"
          data-testid="keepsake-invitation-btn"
        >
          {downloadingInvitation ? (
            <Loader2 size={18} className="animate-spin text-warmgold shrink-0" />
          ) : (
            <FileDown size={18} className="text-warmgold shrink-0" />
          )}
          <div className="min-w-0">
            <p className="text-sm font-medium text-ink leading-tight">Invitación en PDF</p>
            <p className="text-[11px] text-inksoft leading-snug">Portada + ficha de configuración</p>
          </div>
        </button>
      </div>

      {isClosed && (
        <div className="flex items-center gap-2 text-[11px] text-warmgold uppercase tracking-widest pt-1">
          <Lock size={12} /> Invitación cerrada — los invitados ya no pueden abrirla
        </div>
      )}
    </div>
  );
}

